
<template>
  <div class="dashboard-container">
    <!-- Мобильная кнопка для гарпунщиков -->
    <button
      class="mobile-harpooners-toggle"
      @click="mobileHarpooneersExpanded = !mobileHarpooneersExpanded"
    >
      <span class="toggle-icon">🎯</span>
      <span class="toggle-text">Гарпунщики</span>
      <span class="toggle-arrow">{{ mobileHarpooneersExpanded ? '▲' : '▼' }}</span>
    </button>

    <div class="main-content">
      <header class="page-header">
        <div class="header-content">
          <div>
            <h1 class="page-title">Dashboard</h1>
          </div>
          <div class="header-actions">
            <button @click="$emit('refresh')" class="btn-primary">
              <span class="btn-icon">🔄</span>
              Обновить
            </button>
          </div>
        </div>
      </header>

      <!-- Карточки статусов с фильтрами -->
      <div class="stats-grid">
        <div 
          class="stat-card clickable-filter"
          :class="{ 'active-filter': filterStatus === 'unconfirmed' }"
          @click="toggleStatusFilter('unconfirmed')"
        >
          <div class="stat-header">
            <span class="stat-icon">⚠️</span>
            <span class="stat-badge">{{ getOrdersInQueue('unconfirmed') }}</span>
          </div>
          <div class="stat-value">
            <div class="stat-main">{{ getCeilingsInQueue('unconfirmed') }}</div>
            <div class="stat-sub">не подтверждены</div>
          </div>
          <div class="stat-label">Неподтвержденные</div>
          <div class="stat-footer">{{ getOrdersInQueue('unconfirmed') }} заказов</div>
        </div>

        <div 
          class="stat-card clickable-filter"
          :class="{ 'active-filter': filterStatus === 'In_Cut' }"
          @click="toggleStatusFilter('In_Cut')"
        >
          <div class="stat-header">
            <span class="stat-icon cutting">✂️</span>
            <span class="stat-badge">{{ getOrdersInQueue('cut') }}</span>
          </div>
          <div class="stat-value">
            <div class="stat-main">{{ getCeilingsInQueue('cut') }}</div>
            <div class="stat-sub">на раскрое</div>
          </div>
          <div class="stat-label">На раскрое</div>
          <div class="stat-footer">{{ getOrdersInQueue('cut') }} заказов</div>
        </div>

        <div 
          class="stat-card clickable-filter"
          :class="{ 'active-filter': filterStatus === 'In_Garp' }"
          @click="toggleStatusFilter('In_Garp')"
        >
          <div class="stat-header">
            <span class="stat-icon perimeter">📏</span>
            <span class="stat-badge">{{ getOrdersInQueue('harpoon') }}</span>
          </div>
          <div class="stat-value">
            <div class="stat-main">{{ getCeilingsInQueue('harpoon') }}</div>
            <div class="stat-sub">на гарпуне</div>
          </div>
          <div class="stat-label">На гарпуне</div>
          <div class="stat-footer">{{ getOrdersInQueue('harpoon') }} заказов</div>
        </div>

        <div 
          class="stat-card clickable-filter"
          :class="{ 'active-filter': filterStatus === 'In_Pack' }"
          @click="toggleStatusFilter('In_Pack')"
        >
          <div class="stat-header">
            <span class="stat-icon time">📦</span>
            <span class="stat-badge">{{ getOrdersInQueue('pack') }}</span>
          </div>
          <div class="stat-value">
            <div class="stat-main">{{ getCeilingsInQueue('pack') }}</div>
            <div class="stat-sub">на упаковке</div>
          </div>
          <div class="stat-label">На упаковке</div>
          <div class="stat-footer">{{ getOrdersInQueue('pack') }} заказов</div>
        </div>

        <div 
          class="stat-card clickable-filter"
          :class="{ 'active-filter': filterStatus === 'Packed' }"
          @click="toggleStatusFilter('Packed')"
        >
          <div class="stat-header">
            <span class="stat-icon total">✅</span>
            <span class="stat-badge">{{ getOrdersInQueue('completed') }}</span>
          </div>
          <div class="stat-value">
            <div class="stat-main">{{ getCeilingsInQueue('completed') }}</div>
            <div class="stat-sub">упаковано</div>
          </div>
          <div class="stat-label">Упаковано</div>
          <div class="stat-footer">{{ getOrdersInQueue('completed') }} заказов</div>
        </div>
      </div>

      <!-- Фильтры -->
      <div class="filters-section">
        <div class="search-box">
          <span class="search-icon">🔍</span>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Поиск по заказу или клиенту..."
            class="search-input"
          />
        </div>
        <select v-model="filterStatus" class="filter-select">
          <option value="all">🌐 Все этапы</option>
          <option value="unconfirmed">⚠️ Неподтвержденные</option>
          <option value="In_Cut">✂️ На раскрое</option>
          <option value="In_Garp">📏 На гарпуне</option>
          <option value="In_Pack">📦 На упаковке</option>
          <option value="Packed">✅ Упаковано</option>
        </select>

        <select v-model="sortKey" class="filter-select">
          <option value="date_in_desc">📅 Новые первыми</option>
          <option value="total_perimeter_desc">📏 Периметр ↓</option>
          <option value="total_perimeter_asc">📏 Периметр ↑</option>
          <option value="total_ceilings_desc">🔢 Потолков ↓</option>
          <option value="time_minutes_desc">⏱️ Время ↓</option>
        </select>
        <button @click="resetFilters" class="btn-icon-only" title="Сбросить фильтры">
          <span>🔄</span>
        </button>
      </div>

      <!-- Orders Grid -->
      <div class="orders-grid">
        <div
          v-for="order in filteredAndSortedOrders"
          :key="order.IDZakaz"
          class="order-overview-card"
          @click="selectOrder(order)"
        >
          <div class="card-header">
            <div class="order-badge">
              <span class="badge-number">{{ order.NumZakaz }}</span>
            </div>
            <div class="status-indicator" :class="getStatusClass(order.status)">
              {{ getStatusText(order.status) }}
            </div>
          </div>

          <div class="card-body">
            <h3 class="order-client">{{ order.client_name }}</h3>
            <p class="order-id">ID: {{ order.IDClient }}</p>
            
            <div class="metrics-row">
              <div class="metric-small">
                <span class="metric-icon">🏢</span>
                <span class="metric-text">{{ order.total_ceilings }} потолков</span>
              </div>
              <div class="metric-small">
                <span class="metric-icon">📏</span>
                <span class="metric-text">{{ order.total_perimeter.toFixed(1) }} м</span>
              </div>
            </div>

            <!-- Прогресс по очередям -->
            <div class="queue-progress">
              <div v-if="order.has_oper6 === false" class="queue-item unconfirmed">
                <span class="queue-icon">⚠️</span>
                <span class="queue-text">{{ order.total_ceilings }} не подтверждены</span>
              </div>
              <div v-if="getQueueCount(order, 'cut') > 0" class="queue-item">
                <span class="queue-icon">✂️</span>
                <span class="queue-text">{{ getQueueCount(order, 'cut') }} на раскрое</span>
              </div>
              <div v-if="getQueueCount(order, 'harpoon') > 0" class="queue-item">
                <span class="queue-icon">📏</span>
                <span class="queue-text">{{ getQueueCount(order, 'harpoon') }} на гарпуне</span>
              </div>
              <div v-if="getQueueCount(order, 'pack') > 0" class="queue-item">
                <span class="queue-icon">📦</span>
                <span class="queue-text">{{ getQueueCount(order, 'pack') }} на упаковке</span>
              </div>
              <div v-if="getQueueCount(order, 'completed') > 0" class="queue-item completed">
                <span class="queue-icon">✅</span>
                <span class="queue-text">{{ getQueueCount(order, 'completed') }} упаковано</span>
              </div>
            </div>

            <div class="progress-container">
              <div class="progress-info">
                <span class="progress-text">Прогресс</span>
                <span class="progress-percent">{{ order.progress_percent }}%</span>
              </div>
              <div class="progress-track">
                <div 
                  class="progress-bar"
                  :style="{ width: order.progress_percent + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <div class="card-footer">
            <span class="footer-info">
              <span class="info-icon">⏱️</span>
              {{ formatTime(calculateOrderTime(order)) }}
            </span>
            <span class="footer-info">
              <span class="info-icon">📐</span>
              {{ order.total_area.toFixed(1) }} м²
            </span>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredAndSortedOrders.length === 0" class="empty-state">
        <div class="empty-icon">📭</div>
        <h2 class="empty-title">Нет заказов</h2>
        <p class="empty-text">По выбранным фильтрам не найдено ни одного заказа</p>
      </div>
    </div>

    <!-- Боковая панель гарпунщиков -->
    <HarpoonersSidebar
      :harpooners="harpooners"
      :class="{ 'mobile-expanded': mobileHarpooneersExpanded }"
    />

    <!-- Модальное окно -->
    <transition name="modal">
      <div v-if="selectedOrder" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <h2 class="modal-title">Заказ {{ selectedOrder.NumZakaz }}</h2>
            <div class="modal-header-actions" v-if="hasPermission('hide_orders')">
              <!-- Показываем "Скрыто", если заказ уже скрыт -->
              <span v-if="isOrderHidden(selectedOrder.IDZakaz)" class="hidden-badge">
                👁️‍🗨️ Скрыто
              </span>
              <!-- Normal hide button -->
              <button
                v-else-if="!confirmingHideOrder"
                class="btn-hide-order"
                @click="confirmingHideOrder = true"
                :disabled="hiding"
                title="Скрыть заказ"
              >
                <span>👁️‍🗨️</span>
                Скрыть заказ
              </button>
              <!-- Confirmation buttons -->
              <div v-else class="confirm-buttons">
                <span class="confirm-text">Уверены?</span>
                <button
                  class="btn-confirm yes"
                  @click="hideOrder(selectedOrder.IDZakaz)"
                  :disabled="hiding"
                >
                  <span v-if="hiding">⏳</span>
                  <span v-else>✓</span>
                  Да
                </button>
                <button
                  class="btn-confirm no"
                  @click="confirmingHideOrder = false"
                  :disabled="hiding"
                >
                  ✗ Нет
                </button>
              </div>
              <button class="modal-close" @click="closeModal">×</button>
            </div>
          </div>

          <div class="modal-content">
            <div class="modal-stats">
              <div class="modal-stat">
                <div class="modal-stat-value">{{ selectedOrder.client_name }}</div>
                <div class="modal-stat-label">Клиент</div>
              </div>
              <div class="modal-stat" v-if="selectedOrder.marker_name">
                <div class="modal-stat-value">{{ selectedOrder.marker_name }}</div>
                <div class="modal-stat-label">Приоритет</div>
              </div>
              <div class="modal-stat">
                <div class="modal-stat-value">{{ selectedOrder.total_ceilings }}</div>
                <div class="modal-stat-label">Потолков</div>
              </div>
              <div class="modal-stat">
                <div class="modal-stat-value">{{ selectedOrder.total_perimeter.toFixed(1) }} м</div>
                <div class="modal-stat-label">Периметр</div>
              </div>
              <div class="modal-stat">
                <div class="modal-stat-value">{{ selectedOrder.total_area.toFixed(1) }} м²</div>
                <div class="modal-stat-label">Площадь</div>
              </div>
            </div>

            <!-- Прогресс по очередям -->
            <div class="modal-progress-section">
              <h3 class="modal-section-subtitle">Прогресс выполнения</h3>
              <div class="modal-queue-stats">
                <div v-if="selectedOrder.has_oper6 === false" class="modal-queue-item unconfirmed">
                  <div class="modal-queue-header">
                    <span class="modal-queue-icon">⚠️</span>
                    <span class="modal-queue-label">Не подтверждены</span>
                  </div>
                  <div class="modal-queue-count">
                    {{ selectedOrder.total_ceilings }} / {{ selectedOrder.total_ceilings }}
                  </div>
                </div>
                <div class="modal-queue-item">
                  <div class="modal-queue-header">
                    <span class="modal-queue-icon">✂️</span>
                    <span class="modal-queue-label">На раскрое</span>
                  </div>
                  <div class="modal-queue-count">
                    {{ getQueueCount(selectedOrder, 'cut') }} / {{ selectedOrder.total_ceilings }}
                  </div>
                </div>
                <div class="modal-queue-item">
                  <div class="modal-queue-header">
                    <span class="modal-queue-icon">📏</span>
                    <span class="modal-queue-label">На гарпуне</span>
                  </div>
                  <div class="modal-queue-count">
                    {{ getQueueCount(selectedOrder, 'harpoon') }} / {{ selectedOrder.total_ceilings }}
                  </div>
                </div>
                <div class="modal-queue-item">
                  <div class="modal-queue-header">
                    <span class="modal-queue-icon">📦</span>
                    <span class="modal-queue-label">На упаковке</span>
                  </div>
                  <div class="modal-queue-count">
                    {{ getQueueCount(selectedOrder, 'pack') }} / {{ selectedOrder.total_ceilings }}
                  </div>
                </div>
                <div class="modal-queue-item completed">
                  <div class="modal-queue-header">
                    <span class="modal-queue-icon">✅</span>
                    <span class="modal-queue-label">Упаковано</span>
                  </div>
                  <div class="modal-queue-count">
                    {{ getQueueCount(selectedOrder, 'completed') }} / {{ selectedOrder.total_ceilings }}
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-section">
              <h3 class="modal-section-title">
                Потолки <span class="section-count">({{ getCeilingsArray(selectedOrder).length }})</span>
              </h3>
              <div class="ceilings-table">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 50px">#</th>
                      <th>ID</th>
                      <th style="width: 120px">Периметр</th>
                      <th style="width: 120px">Площадь</th>
                      <th style="width: 150px">Фактура</th>
                      <th style="width: 80px">Ширина</th>
                      <th style="width: 150px">Статус</th>
                      <th style="width: 80px">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(ceiling, idx) in getCeilingsArray(selectedOrder)" :key="ceiling.IDPotolok">
                      <td>
                        <div class="ceiling-number">{{ idx + 1 }}</div>
                      </td>
                      <td>
                        <div class="ceiling-id">{{ ceiling.IDPotolok }}</div>
                      </td>
                      <td>
                        <div class="ceiling-value">{{ ceiling.LenHarpoon_m.toFixed(1) }} м</div>
                      </td>
                      <td>
                        <div class="ceiling-value">{{ ceiling.SummClient.toFixed(1) }} м²</div>
                      </td>
                      <td>
                        <div class="ceiling-value">{{ ceiling.factura_name || '-' }}</div>
                      </td>
                      <td>
                        <div class="ceiling-value">{{ ceiling.factura_width || '-' }}</div>
                      </td>
                      <td>
                        <div class="ceiling-status" :class="getCeilingStatusClass(ceiling, selectedOrder.queues, selectedOrder.status)">
                          {{ getCeilingStatusText(ceiling, selectedOrder.queues, selectedOrder.status) }}
                        </div>
                      </td>
                      <td>
                        <div v-if="hasPermission('hide_orders')">
                          <!-- Показываем "Скрыто", если потолок уже скрыт -->
                          <span v-if="isCeilingHidden(ceiling.IDPotolok)" class="hidden-text">
                            Скрыто
                          </span>
                          <!-- Normal hide button -->
                          <button
                            v-else-if="confirmingHideCeiling !== ceiling.IDPotolok"
                            class="btn-hide-ceiling"
                            @click="confirmingHideCeiling = ceiling.IDPotolok"
                            :disabled="hidingCeiling === ceiling.IDPotolok"
                            title="Скрыть потолок"
                          >
                            👁️‍🗨️
                          </button>
                          <!-- Confirmation buttons -->
                          <div v-else class="confirm-buttons-inline">
                            <button
                              class="btn-confirm-small yes"
                              @click="hideCeiling(ceiling.IDPotolok)"
                              :disabled="hidingCeiling === ceiling.IDPotolok"
                              title="Да"
                            >
                              <span v-if="hidingCeiling === ceiling.IDPotolok">⏳</span>
                              <span v-else>✓</span>
                            </button>
                            <button
                            class="btn-confirm-small no"
                            @click="confirmingHideCeiling = null"
                            :disabled="hidingCeiling === ceiling.IDPotolok"
                            title="Нет"
                          >
                            ✗
                          </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import HarpoonersSidebar from './HarpoonersSidebar.vue'
import { usePermissions } from '@/composables/usePermissions'

const API_BASE = 'https://mpapi.premside.uz/api/hidden'

export default {
  name: 'DashboardContent',
  components: {
    HarpoonersSidebar
  },
  setup() {
    const { hasPermission } = usePermissions()
    return {
      hasPermission
    }
  },
  props: {
    orders: {
      type: Array,
      default: () => []
    },
    harpooners: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchQuery: '',
      filterStatus: 'all',
      sortKey: 'date_in_desc',
      selectedOrder: null,
      mobileHarpooneersExpanded: false,
      hiding: false,
      hidingCeiling: null,
      confirmingHideOrder: false,
      confirmingHideCeiling: null,
      hiddenCeilings: [], // Массив ID скрытых потолков
      hiddenOrders: [] // Массив ID скрытых заказов
    }
  },
  computed: {
    filteredAndSortedOrders() {
      let filtered = this.orders;

      // Объединенный поиск по номеру заказа и клиенту
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(o =>
          o.NumZakaz.toLowerCase().includes(query) ||
          o.client_name.toLowerCase().includes(query)
        );
      }

      // Фильтр по очередям
      if (this.filterStatus !== 'all') {
        const queueMap = {
          'In_Cut': 'cut',
          'In_Garp': 'harpoon',
          'In_Pack': 'pack',
          'Packed': 'completed',
          'unconfirmed': 'unconfirmed'
        };
        
        const queueName = queueMap[this.filterStatus];
        
        if (queueName === 'unconfirmed') {
          // Неподтвержденные - это заказы где has_oper6 === false
          filtered = filtered.filter(o => o.has_oper6 === false);
        } else if (queueName) {
          filtered = filtered.filter(o => {
            return o.queues && o.queues[queueName] && o.queues[queueName].length > 0;
          });
        }
      }

      // Сортировка
      let sorted = [...filtered];
      if (this.sortKey === 'date_in_desc') {
        sorted.sort((a, b) => new Date(b.date_in) - new Date(a.date_in));
      } else if (this.sortKey === 'total_perimeter_desc') {
        sorted.sort((a, b) => b.total_perimeter - a.total_perimeter);
      } else if (this.sortKey === 'total_perimeter_asc') {
        sorted.sort((a, b) => a.total_perimeter - b.total_perimeter);
      } else if (this.sortKey === 'total_ceilings_desc') {
        sorted.sort((a, b) => b.total_ceilings - a.total_ceilings);
      } else if (this.sortKey === 'time_minutes_desc') {
        sorted.sort((a, b) => this.calculateOrderTime(b) - this.calculateOrderTime(a));
      }

      return sorted;
    }
  },
  methods: {
    // Получить количество потолков в очереди для заказа
    getQueueCount(order, queueName) {
      if (!order.queues || !order.queues[queueName]) return 0;
      return order.queues[queueName].length;
    },
    // Получить количество ЗАКАЗОВ в очереди (для stat-cards)
    getOrdersInQueue(queueName) {
      if (queueName === 'unconfirmed') {
        // Неподтвержденные - где has_oper6 === false
        return this.orders.filter(o => o.has_oper6 === false).length;
      }
      return this.orders.filter(order => {
        // Исключаем заказы, где все потолки скрыты
        const hasVisibleCeilings = order.queues && order.queues[queueName] &&
          order.queues[queueName].some(c => !this.hiddenCeilings.includes(c.IDPotolok));
        return hasVisibleCeilings;
      }).length;
    },
    // Получить количество ПОТОЛКОВ в очереди (для stat-cards)
    getCeilingsInQueue(queueName) {
      if (queueName === 'unconfirmed') {
        // Неподтвержденные - где has_oper6 === false
        return this.orders
          .filter(o => o.has_oper6 === false)
          .reduce((sum, o) => {
            // Считаем только не скрытые потолки
            const visibleCeilings = o.total_ceilings -
              this.getHiddenCeilingsCount(o);
            return sum + visibleCeilings;
          }, 0);
      }
      let count = 0;
      this.orders.forEach(order => {
        if (order.queues && order.queues[queueName]) {
          // Считаем только не скрытые потолки
          const visibleCeilings = order.queues[queueName].filter(
            c => !this.hiddenCeilings.includes(c.IDPotolok)
          );
          count += visibleCeilings.length;
        }
      });
      return count;
    },
    // Подсчет скрытых потолков в заказе
    getHiddenCeilingsCount(order) {
      if (!order.potolki_detail) return 0;
      const ceilings = Object.values(order.potolki_detail);
      return ceilings.filter(c => this.hiddenCeilings.includes(c.IDPotolok)).length;
    },
    // Проверка, скрыт ли потолок
    isCeilingHidden(ceilingId) {
      return this.hiddenCeilings.includes(ceilingId);
    },
    // Проверка, скрыт ли заказ
    isOrderHidden(orderId) {
      return this.hiddenOrders.includes(orderId);
    },
    // Всего потолков
    getTotalCeilings() {
      return this.orders.reduce((sum, o) => sum + o.total_ceilings, 0);
    },
    // Получить массив потолков
    getCeilingsArray(order) {
      if (!order || !order.potolki_detail) return [];
      return Object.values(order.potolki_detail);
    },
    // Определить статус потолка
    getCeilingStatusText(ceiling, queues, orderStatus) {
      console.log(orderStatus)
      // Проверяем has_oper6 на уровне заказа через selectedOrder
      const order = this.selectedOrder;
      if (order && order.has_oper6 === false) {
        return '⚠️ Не подтвержден';
      }
      
      if (!queues) return '—';
      
      const ceilingId = ceiling.IDPotolok;
      
      if (queues.completed && queues.completed.some(c => c.IDPotolok === ceilingId)) {
        return '✅ Упаковано';
      }
      if (queues.pack && queues.pack.some(c => c.IDPotolok === ceilingId)) {
        return '📦 На упаковке';
      }
      if (queues.harpoon && queues.harpoon.some(c => c.IDPotolok === ceilingId)) {
        return '📏 На гарпуне';
      }
      if (queues.cut && queues.cut.some(c => c.IDPotolok === ceilingId)) {
        return '✂️ На раскрое';
      }
      
      return '—';
    },
    // CSS класс для статуса потолка
    getCeilingStatusClass(ceiling, queues, orderStatus) {
      console.log(orderStatus)
      // Проверяем has_oper6 на уровне заказа
      const order = this.selectedOrder;
      if (order && order.has_oper6 === false) {
        return 'status-unconfirmed';
      }
      
      if (!queues) return '';
      
      const ceilingId = ceiling.IDPotolok;
      
      if (queues.completed && queues.completed.some(c => c.IDPotolok === ceilingId)) {
        return 'status-completed';
      }
      if (queues.pack && queues.pack.some(c => c.IDPotolok === ceilingId)) {
        return 'status-pack';
      }
      if (queues.harpoon && queues.harpoon.some(c => c.IDPotolok === ceilingId)) {
        return 'status-harpoon';
      }
      if (queues.cut && queues.cut.some(c => c.IDPotolok === ceilingId)) {
        return 'status-cut';
      }
      
      return '';
    },
    toggleStatusFilter(status) {
      this.filterStatus = this.filterStatus === status ? 'all' : status;
    },
    getStatusText(status) {
      const statusMap = {
        'unconfirmed': 'Не подтвержден',
        'In_Cut': 'На раскрое',
        'In_Garp': 'На гарпуне',
        'In_Harpoon': 'На гарпуне',
        'In_Pack': 'На упаковке',
        'Packed': 'Упаковано'
      };
      return statusMap[status] || status;
    },
    /**
     * Рассчитать время выполнения заказа с учетом параллельной работы гарпунщиков
     * Каждый гарпунщик может работать только над одним потолком за раз
     */
    calculateOrderTime(order) {
      // Получаем работающих гарпунщиков (at_work === true)
      const workingHarpooners = this.harpooners.filter(h => h.at_work === true);

      // Если нет работающих гарпунщиков, возвращаем 0
      if (workingHarpooners.length === 0) {
        return 0;
      }

      // Берем общий периметр и количество потолков
      const totalPerimeter = order.total_perimeter || 0;
      const totalCeilings = order.total_ceilings || 0;

      if (totalPerimeter === 0 || totalCeilings === 0) {
        return 0;
      }

      // Средний периметр на один потолок
      const avgPerimeterPerCeiling = totalPerimeter / totalCeilings;

      // Распределяем потолки равномерно среди гарпунщиков
      const numHarpooners = workingHarpooners.length;
      const baseCeilings = Math.floor(totalCeilings / numHarpooners);
      const extraCeilings = totalCeilings % numHarpooners;

      // Рассчитываем время для каждого гарпунщика и находим максимальное
      let maxTime = 0;

      for (let i = 0; i < numHarpooners; i++) {
        // Первые extraCeilings гарпунщиков получают на 1 потолок больше
        const assignedCeilings = i < extraCeilings ? baseCeilings + 1 : baseCeilings;

        if (assignedCeilings <= 0) {
          continue;
        }

        // Периметр работы для этого гарпунщика
        const workloadPerimeter = assignedCeilings * avgPerimeterPerCeiling;

        // Скорость гарпунщика (м/ч)
        const speed = workingHarpooners[i].avg_speed || 60;

        // Время в часах, затем в минуты
        const timeHours = workloadPerimeter / speed;
        const timeMinutes = timeHours * 60;

        // Обновляем максимальное время (бутылочное горлышко)
        if (timeMinutes > maxTime) {
          maxTime = timeMinutes;
        }
      }

      return maxTime;
    },
    formatTime(minutes) {
      if (!minutes) return '0 мин';
      const h = Math.floor(minutes / 60);
      const m = Math.round(minutes % 60);
      if (h === 0) return `${m} мин`;
      return `${h}ч ${m}м`;
    },
    selectOrder(order) {
      this.selectedOrder = order;
    },
    closeModal() {
      this.selectedOrder = null;
      this.confirmingHideOrder = false;
      this.confirmingHideCeiling = null;
    },
    resetFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.sortKey = 'date_in_desc';
    },
    getStatusClass(status) {
      return status.toLowerCase().replace('_', '-');
    },
    getProgressStyle(percent) {
      const color = percent === 100 ? '#10b981' : percent > 50 ? '#3b82f6' : '#f59e0b';
      return {
        background: `conic-gradient(${color} ${percent * 3.6}deg, #e5e7eb 0deg)`
      };
    },
    /**
     * Загрузка списка скрытых заказов и потолков
     */
    async loadHiddenItems() {
      try {
        // Загружаем скрытые заказы и потолки параллельно
        const [zakazResponse, potolokResponse] = await Promise.all([
          axios.get(`${API_BASE}/zakaz/hidden`).catch(() => ({ data: { data: [] } })),
          axios.get(`${API_BASE}/potolok/hidden`).catch(() => ({ data: { data: [] } }))
        ])

        // Извлекаем ID скрытых заказов
        if (zakazResponse.data?.data) {
          this.hiddenOrders = zakazResponse.data.data.map(item => item.id_zakaz)
        }

        // Извлекаем ID скрытых потолков
        if (potolokResponse.data?.data) {
          this.hiddenCeilings = potolokResponse.data.data.map(item => item.id_potolok)
        }
      } catch (err) {
        console.error('Error loading hidden items:', err)
      }
    },
    /**
     * Скрыть заказ
     */
    async hideOrder(zakazId) {
      this.hiding = true

      try {
        await axios.post(`${API_BASE}/zakaz/${zakazId}/hide`, {}, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        // Закрываем модал
        this.closeModal()

        // Перезагружаем скрытые элементы
        await this.loadHiddenItems()

        // Обновляем данные
        this.$emit('refresh')
      } catch (err) {
        console.error('Error hiding order:', err)
        alert('Не удалось скрыть заказ: ' + (err.response?.data?.message || err.message))
      } finally {
        this.hiding = false
        this.confirmingHideOrder = false
      }
    },
    /**
     * Скрыть потолок
     */
    async hideCeiling(potolokId) {
      this.hidingCeiling = potolokId

      try {
        await axios.post(`${API_BASE}/potolok/${potolokId}/hide`, {}, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        // Закрываем модал
        this.closeModal()

        // Перезагружаем скрытые элементы
        await this.loadHiddenItems()

        // Обновляем данные
        this.$emit('refresh')
      } catch (err) {
        console.error('Error hiding ceiling:', err)
        alert('Не удалось скрыть потолок: ' + (err.response?.data?.message || err.message))
      } finally {
        this.hidingCeiling = null
        this.confirmingHideCeiling = null
      }
    }
  },
  mounted() {
    // Загружаем список скрытых элементов при монтировании
    this.loadHiddenItems()
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* Мобильная кнопка для гарпунщиков */
.mobile-harpooners-toggle {
  display: none;
}

.main-content {
  flex: 1;
  padding: 0 1rem 1rem 1rem;
  max-width: calc(100% - 16rem);
}

/* Header */
.page-header {
  margin-bottom: 1rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  font-size: 0.75rem;
  color: #6b7280;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.btn-icon {
  font-size: 0.875rem;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.stat-card {
  background: white;
  border-radius: 0.625rem;
  padding: 0.875rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.clickable-filter {
  cursor: pointer;
  position: relative;
}

.clickable-filter.active-filter {
  border: 3px solid #6366f1;
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.3);
  background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%);
}

.clickable-filter.active-filter::before {
  content: '✓';
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  width: 1.125rem;
  height: 1.125rem;
  background: #6366f1;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.625rem;
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.stat-icon {
  font-size: 1.25rem;
}

.stat-badge {
  background: #f3f4f6;
  color: #374151;
  padding: 0.125rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.625rem;
  font-weight: 600;
}

.stat-value {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-bottom: 0.25rem;
}

.stat-main {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-sub {
  font-size: 0.625rem;
  font-weight: 600;
  color: #6b7280;
}

.stat-label {
  font-size: 0.625rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.02rem;
  margin-bottom: 0.25rem;
}

.stat-footer {
  font-size: 0.625rem;
  color: #6b7280;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

/* Filters */
.filters-section {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  flex: 1;
  min-width: 8rem;
}

.search-icon {
  font-size: 0.875rem;
}

.search-input {
  border: none;
  outline: none;
  font-size: 0.75rem;
  width: 100%;
  color: #374151;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.75rem;
  color: #374151;
  cursor: pointer;
  outline: none;
}

.btn-icon-only {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
}

.btn-icon-only:hover {
  background: #f9fafb;
  transform: rotate(180deg);
}

/* Orders Grid */
.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: 0.75rem;
}

.order-overview-card {
  background: white;
  border-radius: 0.625rem;
  padding: 0.875rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.order-overview-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  border-color: #3b82f6;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.order-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0.25rem 0.625rem;
  border-radius: 0.5rem;
}

.badge-number {
  font-weight: 700;
  color: white;
  font-size: 0.75rem;
}

.status-indicator {
  padding: 0.25rem 0.5rem;
  border-radius: 0.75rem;
  font-size: 0.625rem;
  font-weight: 600;
}

.unconfirmed {
  background: #fef3c7;
  color: #92400e;
}

.in-cut {
  background: #fee2e2;
  color: #991b1b;
}

.in-garp,
.in-harpoon {
  background: #fef3c7;
  color: #92400e;
}

.in-pack {
  background: #dbeafe;
  color: #1e40af;
}

.packed {
  background: #d1fae5;
  color: #065f46;
}

.card-body {
  margin-bottom: 0.5rem;
}

.order-client {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.125rem;
}

.order-id {
  font-size: 0.625rem;
  color: #9ca3af;
  margin: 0.125rem 0 0.5rem 0;
}

.metrics-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.metric-small {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.metric-icon {
  font-size: 0.75rem;
}

.metric-text {
  font-size: 0.625rem;
  font-weight: 600;
  color: #374151;
}

/* Прогресс по очередям */
.queue-progress {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 0.5rem 0;
  min-height: 1.25rem;
}

.queue-item {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  padding: 0.125rem 0.375rem;
  background: #f3f4f6;
  border-radius: 0.5rem;
  font-size: 0.625rem;
  font-weight: 500;
  color: #374151;
}

.queue-item.completed {
  background: #d1fae5;
  color: #065f46;
}

.queue-item.unconfirmed {
  background: #fef3c7;
  color: #92400e;
}

.queue-icon {
  font-size: 14px;
}

.queue-text {
  font-size: 12px;
}

.progress-container {
  margin-top: 16px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.progress-text {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.progress-percent {
  font-size: 12px;
  font-weight: 700;
  color: #3b82f6;
}

.progress-track {
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.footer-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.info-icon {
  font-size: 14px;
}

/* Harpooners Sidebar */
/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}

.modal-container {
  background: white;
  border-radius: 1.25rem;
  max-width: 56.25rem;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-hide-order {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #ef4444;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-hide-order:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-hide-order:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.confirm-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.confirm-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.btn-confirm {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-confirm.yes {
  background: #10b981;
  color: #fff;
}

.btn-confirm.yes:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-confirm.no {
  background: #6b7280;
  color: #fff;
}

.btn-confirm.no:hover:not(:disabled) {
  background: #4b5563;
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-close {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  border-radius: 0.625rem;
  cursor: pointer;
  font-size: 1.5rem;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e5e7eb;
  transform: rotate(90deg);
}

.modal-content {
  padding: 1.5rem;
  overflow-y: auto;
}

.modal-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9.375rem, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.modal-stat {
  text-align: center;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 0.75rem;
}

.modal-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
}

.modal-stat-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
}

.modal-progress-section {
  margin: 24px 0;
  padding: 20px;
  background: #f9fafb;
  border-radius: 12px;
}

.modal-section-subtitle {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
}

.modal-queue-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.modal-queue-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
}

.modal-queue-item.completed {
  border-color: #10b981;
  background: #ecfdf5;
}

.modal-queue-item.unconfirmed {
  border-color: #f59e0b;
  background: #fffbeb;
}

.modal-queue-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-queue-icon {
  font-size: 18px;
}

.modal-queue-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.modal-queue-count {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.modal-section {
  margin-top: 24px;
}

.modal-section-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

.section-count {
  color: #6b7280;
  font-weight: 600;
}

.ceilings-table {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.ceilings-table table {
  width: 100%;
  border-collapse: collapse;
}

.ceilings-table thead {
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
}

.ceilings-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ceilings-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
}

.ceilings-table tbody tr:hover {
  background: #f9fafb;
}

.ceilings-table td {
  padding: 12px 16px;
}

.ceiling-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 8px;
  font-weight: 700;
  color: #6b7280;
  font-size: 13px;
}

.ceiling-name {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.ceiling-id {
  font-size: 13px;
  color: #9ca3af;
}

.ceiling-value {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

.ceiling-comments {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.comment-item {
  margin-bottom: 4px;
}

.comment-item:last-child {
  margin-bottom: 0;
}

.comment-label {
  font-weight: 600;
  color: #374151;
}

.ceiling-status {
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  display: inline-block;
}

.status-completed {
  background: #d1fae5;
  color: #065f46;
}

.status-pack {
  background: #dbeafe;
  color: #1e40af;
}

.status-harpoon {
  background: #fef3c7;
  color: #92400e;
}

.status-cut {
  background: #fee2e2;
  color: #991b1b;
}

.status-unconfirmed {
  background: #fef3c7;
  color: #92400e;
}

.btn-hide-ceiling {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
}

.btn-hide-ceiling:hover:not(:disabled) {
  background: #ef4444;
  border-color: #ef4444;
  transform: scale(1.1);
}

.btn-hide-ceiling:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-buttons-inline {
  display: flex;
  gap: 0.25rem;
}

.btn-confirm-small {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-confirm-small.yes {
  background: #10b981;
  color: #fff;
}

.btn-confirm-small.yes:hover:not(:disabled) {
  background: #059669;
  transform: scale(1.1);
}

.btn-confirm-small.no {
  background: #6b7280;
  color: #fff;
}

.btn-confirm-small.no:hover:not(:disabled) {
  background: #4b5563;
  transform: scale(1.1);
}

.btn-confirm-small:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Hidden badges */
.hidden-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.hidden-text {
  font-size: 0.813rem;
  color: #9ca3af;
  font-weight: 500;
  font-style: italic;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 16px;
  color: #6b7280;
}

/* Animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Media Queries для адаптивности */
/* Динамическая ширина main-content в зависимости от sidebar */
.main-content {
  transition: max-width 0.3s ease;
}

/* Tablet и Laptop (1024px и меньше) */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
    gap: 0.5rem;
  }

  .stat-card {
    padding: 0.625rem;
  }

  .stat-icon {
    font-size: 1rem;
  }

  .stat-main {
    font-size: 1.25rem;
  }

  .stat-sub {
    font-size: 0.5625rem;
  }

  .stat-label {
    font-size: 0.5625rem;
  }

  .stat-footer {
    font-size: 0.5625rem;
  }

  .orders-grid {
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
    gap: 0.5rem;
  }

  .order-overview-card {
    padding: 0.625rem;
  }

  .order-client {
    font-size: 0.75rem;
  }

  .metric-text {
    font-size: 0.5625rem;
  }

  .queue-item {
    font-size: 0.5625rem;
    padding: 0.0625rem 0.25rem;
  }
}

/* Mobile (768px и меньше) */
@media (max-width: 768px) {
  .dashboard-container {
    flex-direction: column;
  }

  /* Мобильная кнопка для гарпунщиков */
  .mobile-harpooners-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 1rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: #fff;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    order: -1;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.2);
  }

  .toggle-icon {
    font-size: 1.25rem;
  }

  .toggle-text {
    flex: 1;
    text-align: left;
    margin-left: 0.75rem;
  }

  .toggle-arrow {
    font-size: 0.875rem;
    transition: transform 0.3s;
  }

  /* Гарпунщики по умолчанию скрыты на мобильных */
  .harpooners-sidebar {
    display: none;
    order: -1;
    width: 100% !important;
    border-left: none;
    border-bottom: 1px solid #e5e7eb;
    max-height: 60vh;
    overflow-y: auto;
  }

  .harpooners-sidebar.mobile-expanded {
    display: block;
  }

  /* Скрываем desktop toggle на мобильных */
  .harpooners-sidebar .toggle-btn {
    display: none;
  }

  .main-content {
    max-width: 100%;
    padding: 0.5rem;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .orders-grid {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 1rem;
  }

  .filters-section {
    flex-direction: column;
  }

  .search-box,
  .filter-select {
    width: 100%;
  }
}
</style>